import React, { useState } from 'react'
import classes from './TemplateRoute.module.scss'
import Typography from '@/components/ui/Typography'
import { Header } from '@/components/ui/Header'
import Button from '@/components/ui/Buttons/Button'
import TitleInput from '@/components/ui/TitleInput'
import { Puff } from '@/components/ui/Loaders'
import SettingsButton from '@/components/ui/Buttons/SettingsButton/SettingsButton'
import { ReactComponent as EditorIcon } from '@/assets/icons/editor.svg'
import { ReactComponent as ResultsIcon } from '@/assets/icons/binary-code.svg'
import { ReactComponent as SaveIcon } from '@/assets/icons/save.svg'
import { ReactComponent as CodeIcon } from '@/assets/icons/code-fill.svg'
import { ReactComponent as BackIcon } from '@/assets/icons/chevron-left.svg'
import { useGoBack, usePageParams, useSaveRouteTemplate, useTemplateRouteEditorConfig } from './TemplateRoute.hooks'
import Section from '@/components/ui/Section'
import { ConnectorsMenu, ConnectorModal } from '@/features/connectors'
import { SecretsMenu, SecretModal } from '@/features/secrets'
import { CodeEditor } from '@/features/code-editor'
import { useEditor, useModal, usePasteAtCursor } from '@/app/routes/Route/Route.hooks'
import { SnippetModal, SnippetsMenu } from '@/features/snippets'
import { useProject } from '@/app/routes/Dashboard/Dashboard.hooks'
import { RouteTemplateSettingsModal, templatesQueries } from '@/features/templates'
import { RunButton, RouteLogs, useCodeRunner, useCodeRunnerPayload, PayloadSection } from '@/features/code-runner'
import { useSuspenseQuery } from '@tanstack/react-query'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { GridLayout } from '@/components/GridLayout'
import TrialSection from '@/components/TrialSection'

export const TemplateRoute = () => {
  const modal = useModal()
  const { templateId } = usePageParams()
  const [project] = useProject()
  const {
    data: { template, code = '' },
  } = useSuspenseQuery(templatesQueries.detail(templateId))
  const editor = useEditor(code)
  const [templateName, setTemplateName] = useState(template.name)
  const editorConfig = useTemplateRouteEditorConfig(template)
  const [parentValue, paste] = usePasteAtCursor(editor.code, editor.cursorPosition)
  const goBack = useGoBack()
  const [payload, setPayload] = useCodeRunnerPayload(template.type)
  const codeRunner = useCodeRunner({
    id: template.id,
    code: editor.code,
    payload,
    runtime: template.runtime,
  })

  const [isSaveLoading, save] = useSaveRouteTemplate({
    template,
    code: editor.code,
    name: templateName,
  })

  useDocumentTitle(templateName)

  return (
    <div className={classes.container}>
      <Header>
        <div>
          <Button color={'neutral'} StartIcon={<BackIcon />} onClick={goBack} onAuxClick={goBack}>
            Back
          </Button>
          <TitleInput value={templateName} onSave={setTemplateName} />
        </div>
        <div>
          <Button variant={'soft'} StartIcon={isSaveLoading ? <Puff /> : <SaveIcon />} onClick={save}>
            {isSaveLoading ? 'Saving...' : 'Save'}
          </Button>
          <SettingsButton title={'Template settings'} onClick={() => modal.open('route-settings')} />
        </div>
      </Header>
      <TrialSection className={classes.trialSection} />
      <div className={classes.body}>
        <GridLayout>
          <div key={'editor'}>
            <Section>
              <Section.Header title={'Editor'} icon={<EditorIcon />}>
                <ConnectorsMenu
                  onSelect={(connector) => modal.open('connector', { connector })}
                  onNewSelect={() => modal.open('new-connector')}
                />
                <SecretsMenu
                  projectId={project.id!}
                  onSelect={(secret) => modal.open('secret', { secret })}
                  onNewSelect={() => modal.open('new-secret')}
                />
                <SnippetsMenu
                  onSelect={(snippet) => modal.open('snippet', { snippet })}
                  onNewSelect={() => modal.open('new-snippet')}
                />
                <RunButton onClick={() => codeRunner.run()} isLoading={codeRunner.isPending} />
              </Section.Header>
              <CodeEditor
                config={editorConfig!}
                initialValue={editor.code}
                parentValue={parentValue}
                onModelChange={editor.onModelChange}
                onCursorChanged={editor.onCursorChanged}
                onScrollChange={editor.onScrollChange}
                onSelectionChanged={editor.onSelectionChanged}
              />
            </Section>
          </div>
          <div key={'payload'}>
            <PayloadSection isEnabled={template.type !== 'cron'} value={payload} setValue={setPayload} />
          </div>
          <div key={'results'}>
            <Section>
              <Section.Header title={'Results'} icon={<ResultsIcon />} isActive={codeRunner.isSuccess} />

              <Section.Content isLoading={codeRunner.isPending} isCentered={!codeRunner.isSuccess}>
                {codeRunner.isSuccess ? (
                  <RouteLogs logs={codeRunner.result.messages} />
                ) : codeRunner.isError ? (
                  <>
                    <div className={classes.overlayIcon}>
                      <CodeIcon />
                    </div>
                    <Typography>Error</Typography>
                  </>
                ) : (
                  <>
                    <div className={classes.overlayIcon}>
                      <CodeIcon />
                    </div>
                    <Typography variant={'xl'}>Waiting for code</Typography>
                  </>
                )}
              </Section.Content>

              <Section.Footer>
                <div className={classes.row}>
                  <Typography variant={'caption'}>Response time</Typography>
                  <Typography>{`${codeRunner.result.executionTime} ms`}</Typography>
                </div>
              </Section.Footer>
            </Section>
          </div>
        </GridLayout>
      </div>

      {modal.name === 'snippet' && (
        <SnippetModal {...modal.props} onClose={modal.close} config={editorConfig!} onPaste={paste} />
      )}
      {modal.name === 'new-snippet' && <SnippetModal {...modal.props} onClose={modal.close} config={editorConfig!} />}
      {modal.name === 'secret' && <SecretModal {...modal.props} onClose={modal.close} projectId={project.id} />}
      {modal.name === 'new-secret' && <SecretModal {...modal.props} onClose={modal.close} projectId={project.id} />}
      {modal.name === 'connector' && <ConnectorModal {...modal.props} onClose={modal.close} projectId={project.id} />}
      {modal.name === 'new-connector' && (
        <ConnectorModal {...modal.props} onClose={modal.close} projectId={project.id} />
      )}
      {modal.name === 'route-settings' && (
        <RouteTemplateSettingsModal {...modal.props} onClose={modal.close} templateId={templateId} />
      )}
    </div>
  )
}
